import React, { useEffect, useState, useRef } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SponsorsShowcase from '../components/SponsorsShowcase';
import BackButton from '../components/BackButton';
import { GraduationCap, BookOpen, Award, Users, CheckCircle, ArrowRight, Target, Euro, Shield, Heart, Star, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const useInView = (threshold = 0.1) => {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setIsInView(true); }, { threshold });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, isInView];
};

const FormascopPage = () => {
  const [heroRef, heroInView] = useInView();
  const [offersRef, offersInView] = useInView();
  const [benefitsRef, benefitsInView] = useInView();
  const [creditsRef, creditsInView] = useInView();

  const offers = [
    { icon: Award, title: 'Formations certifiantes', desc: 'Obtenez des certifications reconnues pour valoriser vos compétences.', gradient: 'from-purple-500 to-pink-500' },
    { icon: BookOpen, title: 'Formations qualifiantes', desc: 'Développez vos savoir-faire avec des parcours adaptés à votre métier.', gradient: 'from-blue-500 to-cyan-500' },
    { icon: Target, title: 'Accompagnement reconversion', desc: 'Changez de cap professionnel avec un suivi personnalisé.', gradient: 'from-green-500 to-emerald-500' },
    { icon: Briefcase, title: 'Modules sur-mesure', desc: 'Des formations adaptées aux besoins spécifiques de votre entreprise.', gradient: 'from-amber-500 to-orange-500' },
  ];

  const benefits = [
    { icon: GraduationCap, title: 'Expertise locale', desc: 'Formateurs connaissant les réalités ultramarines' },
    { icon: Euro, title: 'Tarifs solidaires', desc: 'Prix adaptés grâce au modèle coopératif' },
    { icon: Users, title: 'Réseau professionnel', desc: 'Intégrez une communauté de professionnels' },
    { icon: Shield, title: 'Suivi personnalisé', desc: 'Accompagnement avant, pendant et après la formation' },
    { icon: Star, title: 'Qualité certifiée', desc: 'Formations conformes aux standards nationaux' },
    { icon: Heart, title: 'Engagement ESS', desc: 'Participez à l\'économie sociale et solidaire' },
  ];

  const domains = [
    'Bâtiment & Construction', 'Gestion & Administration', 'Management', 
    'Sécurité', 'Habilitations', 'Numérique', 'Langues', 'Santé au travail'
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <Navigation />
      
      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-[80vh] flex items-center overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 pt-20">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1573164574572-cb89e39749b4?w=1920&q=80" 
            alt="Professional training session" 
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-900/90 to-slate-950" />
        </div>
        
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 -left-32 w-[600px] h-[600px] bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 -right-32 w-[500px] h-[500px] bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}} />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-20">
          <BackButton to="/" label="Retour à l'accueil" className="text-white/70 mb-8 hover:text-white transition-colors" />
          
          <div className={`transition-all duration-1000 ${heroInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Badge className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-300 border border-purple-500/30 mb-6 px-4 py-2">
              <GraduationCap className="w-4 h-4 mr-2" /> Centre de Formation Coopératif
            </Badge>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6">
              <span className="bg-gradient-to-r from-white via-purple-100 to-white bg-clip-text text-transparent">FORMA'SCOP</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-slate-300 max-w-4xl mb-4 leading-relaxed font-semibold">
              Former. Qualifier. Accompagner.
            </p>
            
            <p className="text-lg text-slate-400 max-w-3xl mb-10 leading-relaxed">
              Le centre de formation coopératif de l'écosystème <span className="text-purple-400">O'SCOP</span> pour le développement 
              des compétences dans les Outre-mer.
            </p>

            <div className="flex flex-wrap gap-4">
              <Button size="lg" className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold px-8 py-6 text-lg shadow-2xl shadow-purple-500/25 hover:shadow-purple-500/40 transition-all duration-300 hover:scale-105" onClick={() => window.location.href = '/contact'}>
                Découvrir nos formations <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button size="lg" variant="outline" className="border-2 border-white/20 text-white hover:bg-white/10 px-8 py-6 text-lg transition-all duration-300" onClick={() => window.location.href = '/adhesion'}>
                Devenir membre
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Offers Section */}
      <section ref={offersRef} className="py-24 bg-gradient-to-b from-slate-950 to-slate-900 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-1000 ${offersInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Badge className="bg-purple-500/20 text-purple-300 border border-purple-500/30 mb-4 px-4 py-2"><BookOpen className="w-4 h-4 mr-2" /> Notre offre</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Des formations <span className="text-purple-400">adaptées</span></h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {offers.map((offer, index) => (
              <div key={index} className={`group bg-slate-800/30 backdrop-blur-xl rounded-2xl p-8 border border-slate-700/50 hover:border-slate-600 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl ${offersInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: `${index * 100}ms` }}>
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${offer.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg`}>
                  <offer.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{offer.title}</h3>
                <p className="text-slate-400 leading-relaxed">{offer.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Domains Section */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Domaines de formation</h2>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {domains.map((domain, index) => (
              <span key={index} className="bg-slate-800/50 backdrop-blur-xl text-slate-300 px-5 py-3 rounded-xl border border-slate-700/50 hover:border-purple-500/50 hover:text-purple-300 transition-all cursor-default">
                {domain}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Credits System Section */}
      <section ref={creditsRef} className="py-24 bg-gradient-to-b from-slate-900 to-slate-950 relative overflow-hidden">
        <div className="absolute inset-0"><div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-gradient-to-b from-amber-600/10 to-transparent rounded-full blur-3xl" /></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className={`bg-gradient-to-r from-slate-800/80 to-slate-800/40 backdrop-blur-xl rounded-3xl p-8 md:p-12 border border-slate-700/50 transition-all duration-1000 ${creditsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-2xl shadow-amber-500/25">
                <Star className="w-12 h-12 text-white" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <Badge className="bg-amber-500/20 text-amber-300 border border-amber-500/30 mb-3">Innovation coopérative</Badge>
                <h3 className="text-3xl font-bold text-white mb-3">Système de Crédits Formation</h3>
                <p className="text-slate-400 mb-4 leading-relaxed">
                  Les membres de la SCIC bénéficient de crédits de formation mutualisés, 
                  permettant un accès facilité et équitable aux parcours de montée en compétences.
                </p>
                <div className="flex flex-wrap gap-3">
                  {['Crédits mutualisés', 'Accès facilité', 'Tarifs solidaires'].map((feature, i) => (
                    <span key={i} className="flex items-center gap-2 text-sm text-amber-300">
                      <CheckCircle className="w-4 h-4" /> {feature}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section ref={benefitsRef} className="py-24 bg-gradient-to-br from-purple-600 via-pink-600 to-purple-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.1)_1px,transparent_1px)] bg-[size:40px_40px]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Pourquoi <span className="text-purple-200">FORMA'SCOP</span> ?</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className={`bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300 hover:-translate-y-2 text-center ${benefitsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: `${index * 100}ms` }}>
                <div className="w-14 h-14 mx-auto rounded-2xl bg-white/20 flex items-center justify-center mb-4">
                  <benefit.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{benefit.title}</h3>
                <p className="text-sm text-white/70">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-950 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Prêt à <span className="text-purple-400">vous former</span> ?</h2>
          <p className="text-xl text-slate-400 mb-10">Découvrez notre catalogue et trouvez la formation adaptée à vos besoins.</p>
          <Button size="lg" className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold px-10 py-6 text-lg shadow-2xl shadow-purple-500/25 hover:shadow-purple-500/40 transition-all duration-300 hover:scale-105" onClick={() => window.location.href = '/contact'}>
            Nous contacter <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      <SponsorsShowcase variant="branch" branche="FORMASCOP" />
      <Footer />
    </div>
  );
};

export default FormascopPage;
